import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import { UntypedFormBuilder, FormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash-es';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { F1125SchEService } from './f1125-sch-e.service';

@Component({
    selector: 'gtw-f1125-sch-e',
    templateUrl: './f1125-sch-e.component.html',
    styleUrls: ['./f1125-sch-e.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F1125SchEComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};
  
  filterParams: any = {};
  
  globalService: any;
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };
  GENERAL_CONFIG: any;

  // Row data from AngularJS (similar to F8916 pattern)
  rowData: any = {};

  // Component properties migrated from F1125ECtrl.js
  modalTitle = 'Compensation of Officers';
  
  // Tab configuration 
  tabs = [
    { id: 1, name: "Form Entry", active: true }
  ];
  activeTab = 1;
  currentTabName = "Form Entry";
  
  // Modal minimization state
  minimise = false;
  originalData: any[] = [];
  userSettings: any;
  logged_in_user: string = '';
  leId: string = '';
  formName = '1120';
  hgt = '96%';
  cdr_no: string = '';
  leType: string = '';
  systemFlag = 'D';
  
  // Form data structures
  formData: any = {
    "addrows": [],
    "editrows": [],
    "removerows": []
  };
  
  entityList: any[] = [];
  saveParamDtls: any[] = [];
  formLOV: any[] = [];
  tableHeader: any[] = [];
  form1125EView: any[] = [];
  
  // Loading states
  loadingData = true;
  crudLoading = false;
  isSaveClicked = false;

  constructor(
    private jsonObjectFactory: JsonObjectFactoryService,
    private f1125SchEService: F1125SchEService,
    private _snackBar: MatSnackBar, private gtwSnackbar: GtwSnackbarService,
    private formBuilder: UntypedFormBuilder,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.appService = {
      USER_SETTINGS: header.userSettingsData || {},
      SERVER_CONFIG: header.serverConfigData || {},
    };
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.globalService.filterParams = this.filterParams;
    this.cdr.markForCheck();
  }

  init() {
    try {
      if (!this.globalService || !this.GENERAL_CONFIG) {
        return;
      }

      this.userSettings = this.appService.USER_SETTINGS;
      this.logged_in_user = this.userSettings?.user?.sso_id || '';
      
      // Only proceed if we have row data
      if (this.rowData && Object.keys(this.rowData).length > 0) {
        this.setupComponentData();
        this.initializeComponent();
      }
    } catch (e) {
      console.error('Error in F1125SchE init:', e);
    }
  }

  private setupComponentData() {
    // Use real data from rowData parameter (like in original F8916 pattern)
    this.leId = this.rowData.LEID || '';
    this.cdr_no = this.rowData.MARS || '';
    this.leType = this.rowData.LE_TYPE || '';
    
    this.entityList = [
      {key: "Tax Year", value: this.rowData.TAX_YEAR || ''},
      {key: "LEID", value: this.rowData.LEID || ''},
      {key: "LE Name", value: this.rowData.LE_NAME || ''}
    ];
  }

  initializeComponent() {
    // First fetch LOV data, then form data (like in original controller)
    this.fetchLOVData();
  }

  fetchFormData() {
    this.loadingData = true;
    
    const params = {
      "action_id": 31333,
      "tax_year": this.rowData.TAX_YEAR,
      "scenario": this.rowData.SCENARIO,
      "jcd_key": this.rowData.JCD_KEY,
      "combination_key": this.rowData.COMBINATION_KEY,
      "form_name": "1125E",
      "schedule_name": "NA",
      "part_no": "NA"
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadPivotFormJson',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.processFormData(data);
      this.loadingData = false;
      this.cdr.markForCheck();
    });
  }

  private processFormData(data: any) {
    if (data && data.irsForm && data.irsForm.formItemList && data.irsForm.formItemList.length > 0) {
      const formItemList = data.irsForm.formItemList;
      this.form1125EView = [];
      
      // Process each form item (starting from index 1, skipping header)
      for (let i = 1; i < formItemList.length; i++) {
        const formItem = formItemList[i];
        if (formItem.dataRowList && formItem.dataRowList.length > 0) {
          const dataRow = formItem.dataRowList[0];
          const saveParams = dataRow.rowDimensions.object_id.split('~');
          const dataCells = dataRow.dataCellList;
          
          const tempDataObj: any = {
            num: dataCells[0]?.name || '',
            description: dataCells[1]?.name || '',
            line_order: saveParams[8],
            saveParams: saveParams
          };

          // Process additional data cells based on count
          if (dataCells.length > 2) {
            this.iterateDataCells(dataCells.length, dataCells, tempDataObj, saveParams);
          }

          this.form1125EView.push(tempDataObj);
        }
      }

      // Filter out header rows and get table headers
      this.tableHeader = this.form1125EView.filter(item => item.num === 'NA');
      this.form1125EView = this.form1125EView.filter(item => item.num !== 'NA');

      // Setup table header values
      if (this.form1125EView.length > 0) {
        // Check if dropdown value exists in LOV
        if (this.form1125EView[0].value_1) {
          const dropDownVal = this.formLOV.filter(item => item.CODE_DESC === this.form1125EView[0].value_1);
          
          if (!dropDownVal || dropDownVal.length === 0) {
            // Handle "Other" option
            this.form1125EView[0].conditionalField = true;
            this.form1125EView[0].value_user_input = this.form1125EView[0].value_1;
            this.form1125EView[0].value_1 = 'Other';
          }
        }

        // Setup table headers
        this.tableHeader = [
          {
            description: "Name of the Officer",
            value: this.form1125EView[0].conditionalField ? 
                   this.form1125EView[0].value_user_input : 
                   this.form1125EView[0].value_1,
            data_type: "text"
          },
          {
            description: "Amount of Compensation",
            value: this.form1125EView[0].value || '',
            data_type: "text"
          }
        ];
      }

      // Store original data
      this.originalData = _.cloneDeep(this.form1125EView);
    }
  }

  private iterateDataCells(count: number, dataCells: any[], tempDataObj: any, saveParams: any[]) {
    if (count === 3) {
      // Single attribute
      const cell = dataCells[2];
      tempDataObj.lineAttrKey_a = cell.key.split('~')[1];
      tempDataObj.attrib_name_a = cell.key.split('~')[0];
      tempDataObj.attrib_order_a = cell.key.split('~')[2];
      tempDataObj.is_form_display = saveParams[9];
      tempDataObj.edit_type = cell.property?.edit_type;
      tempDataObj.data_type = cell.property?.data_type;
      tempDataObj.disableField = false; // Default to editable
      
      if (cell.value == null || cell.value === "0") {
        tempDataObj.value = '';
      } else {
        tempDataObj.value = cell.value;
      }
    } else if (count === 4) {
      // Dropdown + Amount attributes
      const dropdownCell = dataCells[2];
      const amountCell = dataCells[3];
      
      // Dropdown
      tempDataObj.lineAttrKey_a = dropdownCell.key.split('~')[1];
      tempDataObj.attrib_name_a = dropdownCell.key.split('~')[0];
      tempDataObj.attrib_order_a = dropdownCell.key.split('~')[2];
      tempDataObj.value_1 = dropdownCell.value;
      tempDataObj.edit_type = dropdownCell.property?.edit_type;
      tempDataObj.data_type = dropdownCell.property?.data_type;
      tempDataObj.conditionalField = false;

      // Amount - CRITICAL: Like AngularJS, this field should be disabled
      tempDataObj.lineAttrKey_b = amountCell.key.split('~')[1];
      tempDataObj.attrib_name_b = amountCell.key.split('~')[0];
      tempDataObj.attrib_order_b = amountCell.key.split('~')[2];
      tempDataObj.disableField = true; // 🔥 Like AngularJS line 149: tempDataObj.disableField = true;
      
      if (amountCell.value == null || amountCell.value === "0") {
        tempDataObj.value = '';
      } else {
        tempDataObj.value = amountCell.value;
      }
    }
  }

  fetchLOVData() {
    const params = {
      "action_code": "4swkay",
      "sso_id": this.logged_in_user,
      "tax_year": this.rowData.TAX_YEAR,
      "scenario": this.rowData.SCENARIO,
      "jcd_key": this.rowData.JCD_KEY,
      "form_no": "1125E",
      "schedule_name": "NA"
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      if (data && data.jsonObject) {
        this.formLOV = data.jsonObject;
      }
      // After LOV is loaded, fetch form data
      this.fetchFormData();
      this.cdr.markForCheck();
    });
  }

  updateColumnB(selectedValue: string) {
    // Update logic when dropdown selection changes
    if (this.form1125EView.length > 0) {
      this.form1125EView[0].value_1 = selectedValue;
      this.form1125EView[0].conditionalField = selectedValue === 'Other';
      
      // Update table header
      this.tableHeader[0].value = selectedValue;
    }
  }

  updateField(lineNum: string, dataType: string, selectedValue: string) {
    // Handle field updates
    const rowIndex = this.form1125EView.findIndex(item => item.num === lineNum);
    if (rowIndex >= 0) {
      const row = this.form1125EView[rowIndex];
      
      if (dataType === 'number') {
        // Validate numeric input
        const numValue = parseFloat(row.value);
        if (!isNaN(numValue)) {
          row.value = numValue.toString();
        }
      }
      
      // Update table header if it's the first row
      if (lineNum === '1') {
        this.tableHeader[0].value = selectedValue;
        this.tableHeader[1].value = row.value;
      }
    }
  }

  save() {
    this.isSaveClicked = true;
    this.crudLoading = true;

    // Prepare save data
    const tempArray: any[] = [];

    for (let i = 0; i < this.form1125EView.length; i++) {
      const row = this.form1125EView[i];
      
      // Form row data A
      const formRowDataA = {
        tax_year: this.entityList.find(item => item.key === "Tax Year")?.value,
        trans_type_key: row.saveParams[1],
        line_order: row.line_order,
        attrib_order: row.attrib_order_a,
        line_description: row.description,
        combination_key: this.rowData.COMBINATION_KEY,
        group_obj_key: this.rowData.GROUP_OBJ_KEY,
        form_key: row.saveParams[2],
        line_no: row.num,
        occurence: row.saveParams[7],
        line_attrib_key: row.lineAttrKey_a,
        Attribute_Name: row.attrib_name_a,
        Attribute_Value: row.value_1 === 'Other' ? row.value_user_input : row.value_1,
        trans_dtls_key: row.saveParams[3],
        trans_status: row.saveParams[6],
        is_dirty: '',
        JSON_DATA: []
      };
      tempArray.push(formRowDataA);

      // Form row data B (if exists)
      if (row.attrib_name_b) {
        const formRowDataB = {
          tax_year: this.entityList.find(item => item.key === "Tax Year")?.value,
          trans_type_key: row.saveParams[1],
          line_order: row.line_order,
          attrib_order: row.attrib_order_b,
          line_description: row.description,
          combination_key: this.rowData.COMBINATION_KEY,
          group_obj_key: this.rowData.GROUP_OBJ_KEY,
          form_key: row.saveParams[2],
          line_no: row.num,
          occurence: row.saveParams[7],
          line_attrib_key: row.lineAttrKey_b,
          Attribute_Name: row.attrib_name_b,
          Attribute_Value: row.value,
          trans_dtls_key: row.saveParams[3],
          trans_status: row.saveParams[6],
          is_dirty: ''
        };
        tempArray.push(formRowDataB);
      }
    }

    this.formData.editrows = [tempArray];
    const mainData = [this.formData];

    this.f1125SchEService.saveF1125Form(
      this.GENERAL_CONFIG.base_url,
      mainData,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.crudLoading = false;
        this.isSaveClicked = false;
        console.log('F1125SchE save response:', response);
        
        if (response.errorMessage && response.errorMessage !== 'null') {
          this.gtwSnackbar.openSnackBar(response.errorMessage, 'gtw-snackbar--red', true, 4);
        } else {
          // Show success snackBar
          this.gtwSnackbar.openSnackBar('Form saved successfully', 'gtw-snackbar--green', true, 4);
          
          // Emit using @Output (AngularJS will close modal automatically)
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.crudLoading = false;
        this.isSaveClicked = false;
        this.gtwSnackbar.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red', true, 4);
        this.cdr.markForCheck();
      }
    );
  }

  reset() {
    // Reset form to original data
    this.form1125EView = _.cloneDeep(this.originalData);
    if (this.form1125EView.length > 0) {
      this.tableHeader[0].value = this.form1125EView[0].value_1;
      this.tableHeader[1].value = this.form1125EView[0].value;
    }
  }

  cancel() {
    // Emit cancel event to close modal (AngularJS integration)
    this.requestGridUpdate.emit('cancel');
  }

  // Track by function for ngFor
  itemTracker(index: number, item: any): any {
    return item ? item.num || index : index;
  }
} 